import { useEffect, useMemo, useState } from "react";
import untruncateJson from "untruncate-json";
import { CodeEditor, type PinnedRange } from "../CodeEditor";
import { type JsonShape, JsonTable } from "./JsonTable";
import type { FileRenderControl, FileSelection } from "./types";

const filePath = "output.json";

type Props = {
  output: string;
  bleed?: number;
  selectedRanges?: readonly PinnedRange[];
  onSelect?: (selections: FileSelection[]) => void;
  onControls?: (controls: FileRenderControl[]) => void;
};

const prettyPrint = (json: string) => {
  try {
    return JSON.stringify(JSON.parse(untruncateJson(json)), null, 2);
  } catch {
    return json;
  }
};

function classifyJson(raw: string): JsonShape {
  try {
    const parsed = JSON.parse(untruncateJson(raw));

    if (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed.every(
        (item) =>
          typeof item === "object" && item !== null && !Array.isArray(item)
      )
    ) {
      const columnSet = new Set<string>();
      for (const item of parsed) {
        for (const key of Object.keys(item as Record<string, unknown>)) {
          columnSet.add(key);
        }
      }
      return {
        kind: "array-of-objects",
        data: parsed as Record<string, unknown>[],
        columns: [...columnSet],
      };
    }

    if (
      typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed)
    ) {
      return { kind: "single-object", data: parsed as Record<string, unknown> };
    }
  } catch {
    // fall through
  }
  return { kind: "other" };
}

export function Json({
  output,
  bleed,
  selectedRanges,
  onSelect,
  onControls,
}: Props) {
  const [showTable, setShowTable] = useState(
    () => localStorage.getItem("jsonViewMode") === "table"
  );
  const shape = useMemo(() => classifyJson(output), [output]);
  const canShowTable = shape.kind !== "other";
  const tableVisible = showTable && canShowTable;
  const formatted = prettyPrint(output);

  useEffect(() => {
    if (!onControls || !canShowTable) return;
    onControls([
      {
        id: "json-view",
        icon: "table",
        activeIcon: "square-code",
        label: tableVisible ? "View as JSON" : "View as table",
        active: tableVisible,
        onToggle: () => {
          setShowTable(!tableVisible);
          localStorage.setItem("jsonViewMode", tableVisible ? "code" : "table");
        },
      },
    ]);
    return () => onControls([]);
  }, [onControls, canShowTable, tableVisible]);

  return (
    <>
      {!tableVisible && (
        <CodeEditor
          filePath={filePath}
          value={formatted}
          onSelectionChange={
            onSelect &&
            ((selections) =>
              onSelect(
                selections.map((s) => ({
                  from: s.from,
                  to: s.to,
                  fromLine: s.fromLine,
                  toLine: s.toLine,
                  text: s.text,
                }))
              ))
          }
          pinnedRanges={selectedRanges}
        />
      )}
      {tableVisible && (
        <div
          style={
            bleed
              ? {
                  margin: -bleed,
                  width: `calc(100% + ${bleed * 2}px)`,
                  height: `calc(100% + ${bleed * 2}px)`,
                }
              : { width: "100%", height: "100%" }
          }
        >
          <JsonTable shape={shape} />
        </div>
      )}
    </>
  );
}
